import { openDB, type DBSchema, type IDBPDatabase } from "idb";

/**
 * Local-first IndexedDB layer for in-flight editor drafts.
 *
 * Why: the BE save lease can expire (network blip, tab sleep, MinIO presigned
 * URL > expiry) but the user has typed real work. We persist the raw bytes
 * to IndexedDB on every debounced change so we can replay the unsaved
 * buffer on next mount, regardless of lease state. This is also what powers
 * the future "X unsaved drafts — sync now?" tray.
 *
 * Schema rules:
 *  - One row per `session_id` (Drive item edited twice = two leases =
 *    two rows; we never overwrite cross-session).
 *  - `base_etag` snapshots what the BE thought the file was when we leased
 *    it — used to detect "the doc moved under us" on save.
 *  - `dirty_since` is `null` once the upstream save acks; non-null means we
 *    have local-only bytes to flush.
 *  - `ai_proposals` lives next to the bytes so HITL suggestions survive a
 *    refresh — accepting/rejecting a proposal mutates the same row.
 *
 * Keep this module DOM-only (no React) so the chat-side reconciliation
 * service can call it from a SharedWorker if we promote drafts to a
 * cross-tab queue in phase 2.
 */

/** Persisted AI-suggested mutation that the user has not yet accepted/rejected. */
export interface AIProposal {
  /** Stable id — also the chat message id that emitted it, so the inbox can backlink. */
  id: string;
  /** Discriminator for the action — drives which editor adapter renders it. */
  kind: "docx_replace_range" | "xlsx_set_cell";
  /** Action-specific payload (range coords, replacement text, cell address, value). */
  payload: unknown;
  /** Optional human-readable "why" — surfaced in the tracked-change tooltip. */
  rationale?: string;
  /** `null` = pending, `true` = accepted, `false` = rejected. Sticky once set. */
  accepted: boolean | null;
}

/** One IndexedDB row — a self-contained snapshot of the editing tab. */
export interface EditorDraft {
  /** Maps 1:1 to {@link EditSession.session_id} — also the object-store key. */
  session_id: string;
  /** Canonical doc id (back-reference for the inbox / Drive view). */
  doc_id: string;
  /** MIME of the bytes (so a generic restorer can pick the right adapter). */
  mime_type: string;
  /** Raw editor bytes (`.docx` / `.xlsx`). Stored as Blob because IDB copies
   * Blobs by reference (no JSON serialisation cost). */
  blob: Blob;
  /**
   * ETag at lease time — `If-Match` payload when we flush. `null` when the
   * BE didn't surface one (legacy upload, freshly created artefact); in
   * that case the editor saves without optimistic concurrency.
   */
  base_etag: string | null;
  /** `null` once the upstream save succeeded; epoch-ms otherwise. */
  dirty_since: number | null;
  /** Frozen so callers cannot mutate the array in-place (mutate via saveDraft). */
  ai_proposals: ReadonlyArray<AIProposal>;
}

interface DraftsDBSchema extends DBSchema {
  drafts: {
    key: string;
    value: EditorDraft;
    indexes: {
      /** Lets `listDirtyDrafts` skip the synced rows without a full scan. */
      "by-dirty": number;
    };
  };
}

const DB_NAME = "ando-editor-drafts-v1";
const DB_VERSION = 1;
const STORE = "drafts" as const;
const DIRTY_INDEX = "by-dirty" as const;

/** Module-level singleton — IDB connection is cheap to share across editors. */
let _dbPromise: Promise<IDBPDatabase<DraftsDBSchema>> | null = null;

/**
 * Open (or create) the drafts database. Idempotent — every call after the
 * first returns the same connection promise so concurrent editors don't
 * race the upgrade transaction.
 */
export function openDraftsDB(): Promise<IDBPDatabase<DraftsDBSchema>> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = openDB<DraftsDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // First-run install — keep this branch the single source of truth
      // for the v1 schema. Schema changes bump DB_VERSION + add migrations.
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "session_id" });
        // `0` for synced rows so an index range can target "dirty only".
        store.createIndex(DIRTY_INDEX, "dirty_since");
      }
    },
    blocked() {
      // Another tab is holding the previous schema — let the dev console
      // surface it. Don't throw: the open() promise will retry naturally.
      console.warn("[editor-drafts] another tab is blocking the IDB upgrade");
    },
    terminated() {
      // Browser killed the connection (private mode quota, OS sleep). Drop
      // the singleton so the next call reopens it.
      _dbPromise = null;
    },
  });
  return _dbPromise;
}

/**
 * Load the draft for `session_id`, or `undefined` if none exists yet.
 * Caller uses the absence to drive the "fetch from server" fallback.
 */
export async function loadDraft(
  session_id: string,
): Promise<EditorDraft | undefined> {
  const db = await openDraftsDB();
  return db.get(STORE, session_id);
}

/**
 * Upsert a draft row. The caller is expected to compute `dirty_since`
 * (epoch-ms on local change, `null` after a successful flush). We do not
 * deep-clone `blob` — Blobs are immutable so a shared reference is safe.
 */
export async function saveDraft(draft: EditorDraft): Promise<void> {
  const db = await openDraftsDB();
  await db.put(STORE, draft);
}

/**
 * Remove a draft once the BE confirms a write (or the user discards the
 * lease). Idempotent — deleting a missing key is a no-op.
 */
export async function deleteDraft(session_id: string): Promise<void> {
  const db = await openDraftsDB();
  await db.delete(STORE, session_id);
}

/**
 * Enumerate every draft with `dirty_since != null` — i.e. local-only bytes
 * that haven't been flushed upstream. The future background-sync worker
 * polls this to retry saves after the lease renews.
 */
export async function listDirtyDrafts(): Promise<EditorDraft[]> {
  const db = await openDraftsDB();
  // IDB can't index "where dirty_since IS NOT NULL" directly; we use a
  // lowerOpen range from epoch 1 which covers every non-null ms timestamp.
  const range = IDBKeyRange.lowerBound(1);
  return db.getAllFromIndex(STORE, DIRTY_INDEX, range);
}
